import React, { useEffect, useState, useMemo } from 'react';
import { 
  Typography, Button, Table, Tag, Spin, Input, Select, message
} from 'antd';
import { 
  MailOutlined, MessageOutlined, PlusOutlined, 
  SearchOutlined, EditOutlined, DeleteOutlined, 
  StopOutlined, PlayCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Text } = Typography;

// Status tag component
const StatusTag = ({ status }) => {
  const statusConfig = {
    active: { color: '#10b981', bg: '#d1fae5', text: 'Active' },
    paused: { color: '#f59e0b', bg: '#fef3c7', text: 'Paused' },
    scheduled: { color: '#8b5cf6', bg: '#ede9fe', text: 'Scheduled' },
    draft: { color: '#6b7280', bg: '#f3f4f6', text: 'Draft' },
    completed: { color: '#3b82f6', bg: '#dbeafe', text: 'Completed' },
    sent: { color: '#10b981', bg: '#d1fae5', text: 'Sent' },
    delivered: { color: '#10b981', bg: '#d1fae5', text: 'Delivered' }
  };
  const config = statusConfig[status] || statusConfig.draft;
  return (
    <Tag style={{ background: config.bg, color: config.color, border: 'none', fontWeight: 500 }}>
      {config.text}
    </Tag>
  );
};

// Campaign Section Component
const CampaignSection = ({ title, icon, color, campaigns, columns, count }) => {
  return (
    <div style={{ marginBottom: 32 }}>
      {/* Section Header */}
      <div style={{ 
        background: '#fff', 
        padding: '12px 16px', 
        borderRadius: '8px 8px 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        border: '1px solid #e5e7eb',
        borderBottom: 'none'
      }}>
        <span style={{ color: color, fontSize: 20 }}>{icon}</span>
        <Title level={5} style={{ margin: 0, color: '#1f2937' }}>{title}</Title>
        <Text style={{ color: '#6b7280', marginLeft: 8 }}>
          {count} campaigns
        </Text>
      </div>
      
      {/* Table */}
      <Table
        dataSource={campaigns}
        columns={columns}
        rowKey="id"
        pagination={false}
        size="small"
        style={{ 
          border: '1px solid #e5e7eb',
          borderTop: 'none',
          borderRadius: '0 0 8px 8px'
        }}
        scroll={{ x: 1200 }}
      />
    </div>
  );
};

const Dashboard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');

  const authHeader = () => (token ? { Authorization: `Bearer ${token}` } : {});

  // Campaign action functions
  const deleteCampaign = async (campaignId, campaignName, source) => {
    if (!window.confirm(`Are you sure you want to delete campaign "${campaignName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const apiBase = source === 'crm' ? '/api/crm/campaigns' : '/api/vehicle-campaigns';
      const { data } = await axios.delete(`${apiBase}/${campaignId}`, { headers: authHeader() });
      const syncFailures = (data?.platform_sync || []).filter(r => r.applicable && !r.success);
      if (syncFailures.length > 0) {
        message.warning(`Campaign removed from Pathmarkr, but couldn't be deleted on ${syncFailures.map(f => f.platform).join(', ')}: ${syncFailures[0].error}`);
      } else {
        message.success('Campaign deleted successfully');
      }
      fetchCampaigns();
    } catch (error) {
      console.error('Failed to delete campaign:', error);
      message.error('Failed to delete campaign. Please try again.');
    }
  };

  const stopCampaign = async (campaignId, campaignName, currentStatus, source) => {
    const action = currentStatus === 'active' ? 'pause' : 'reactivate';
    const confirmMessage = currentStatus === 'active' 
      ? `Are you sure you want to pause campaign "${campaignName}"?`
      : `Are you sure you want to reactivate campaign "${campaignName}"?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      const apiBase = source === 'crm' ? '/api/crm/campaigns' : '/api/vehicle-campaigns';
      const method = source === 'crm' ? 'put' : 'patch';
      const { data } = await axios[method](`${apiBase}/${campaignId}`,
        { status: newStatus },
        { headers: authHeader() }
      );
      const syncFailures = (data?.platform_sync || []).filter(r => r.applicable && !r.success);
      if (syncFailures.length > 0) {
        message.warning(`Status updated in Pathmarkr, but couldn't ${action} on ${syncFailures.map(f => f.platform).join(', ')}: ${syncFailures[0].error}`);
      } else {
        message.success(`Campaign ${action}d successfully`);
      }
      fetchCampaigns();
    } catch (error) {
      console.error(`Failed to ${action} campaign:`, error);
      message.error(`Failed to ${action} campaign. Please try again.`);
    }
  };

  const editCampaign = (campaign) => {
    // Navigate to edit page - adjust path as needed based on your routing
    navigate(`/client/campaigns/edit/${campaign.id}`);
  };

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      // Fetch vehicle campaigns
      const { data } = await axios.get('/api/vehicle-campaigns', { headers: authHeader() });
      const vcItems = (Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [])
        .map(c => ({ ...c, _source: 'vehicle' }));

      // Fetch CRM email campaigns
      let crmItems = [];
      try {
        const { data: crmData } = await axios.get('/api/crm/campaigns', { headers: authHeader() });
        const rawCrm = Array.isArray(crmData?.campaigns) ? crmData.campaigns : Array.isArray(crmData) ? crmData : [];
        crmItems = rawCrm.map(c => ({
          ...c,
          _source: 'crm',
          message_type: c.message_type || 'email',
          campaign_type: c.message_type || 'email',
        }));
      } catch (crmErr) {
        console.warn('CRM campaigns fetch failed:', crmErr.message);
      }

      setCampaigns([...vcItems, ...crmItems]);
    } catch (e) {
      console.error('Failed to fetch campaigns:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Group campaigns by platform
  const groupedCampaigns = useMemo(() => {
    const groups = {
      meta: [],
      instagram: [],
      google: [],
      email: [],
      sms: []
    };

    campaigns.forEach(campaign => {
      const adPlatform = campaign.ad_platform || campaign.platform;
      const messageType = campaign.message_type || campaign.type;
      const campaignType = campaign.campaign_type || campaign.campaignType;
      const routeData = campaign.route_data || {};
      const channel = routeData.channel;

      // Check route_data.channel first (ProximityIQ campaigns take priority)
      if (channel === 'email') {
        groups.email.push(campaign);
      } else if (channel === 'sms') {
        groups.sms.push(campaign);
      } else if (channel === 'both') {
        // If both, add to both email and SMS
        groups.email.push(campaign);
        groups.sms.push(campaign);
      }
      // Check ad platform for stop/route campaigns (only if no channel specified)
      else if (adPlatform === 'meta' || adPlatform === 'facebook') {
        groups.meta.push(campaign);
      } else if (adPlatform === 'instagram') {
        groups.instagram.push(campaign);
      } else if (adPlatform === 'google' || adPlatform === 'google_ads') {
        groups.google.push(campaign);
      }
      // Check message type for email/SMS
      else if (messageType === 'email' || campaignType === 'email') {
        groups.email.push(campaign);
      } else if (messageType === 'sms' || campaignType === 'sms' || campaignType === 'SMS') {
        groups.sms.push(campaign);
      }
      // Check route_data for email/SMS indicators
      else if (routeData.email_body || routeData.email_subject) {
        groups.email.push(campaign);
      } else if (routeData.sms_message) {
        groups.sms.push(campaign);
      }
      // Check for SMS-specific indicators
      else if (campaign.sms_message || campaign.smsMessage || campaign.phone_number || campaign.phoneNumber) {
        groups.sms.push(campaign);
      }
      // Check for email-specific indicators
      else if (campaign.email_template || campaign.emailTemplate || campaign.subject_line || campaign.subjectLine) {
        groups.email.push(campaign);
      }
      // Check for google_ads_config or meta_ads_config as additional signals
      else if (campaign.google_ads_config) {
        groups.google.push(campaign);
      } else if (campaign.meta_ads_config) {
        groups.meta.push(campaign);
      }
      // Default fallback - if nothing else matches, check for social media keywords
      else if (campaignType && campaignType.toLowerCase().includes('sms')) {
        groups.sms.push(campaign);
      } else if (campaignType && campaignType.toLowerCase().includes('email')) {
        groups.email.push(campaign);
      } else if (campaignType && campaignType.toLowerCase().includes('google')) {
        groups.google.push(campaign);
      } else {
        // Truly unrecognized campaigns - default to meta
        groups.meta.push(campaign);
      }
    });

    return groups;
  }, [campaigns]);

  // Column definitions for Meta/Google campaigns
  const adColumns = [
    { 
      title: 'Campaign Name', 
      dataIndex: 'name', 
      key: 'name',
      width: 200,
      fixed: 'left',
      render: (v) => <Text strong style={{ color: '#1877f2' }}>{v || 'Untitled'}</Text>
    },
    { 
      title: 'Location', 
      dataIndex: 'location', 
      key: 'location',
      width: 120,
      render: (v) => <Text type="secondary">{v || '-'}</Text>
    },
    { 
      title: 'Type', 
      dataIndex: 'campaign_type', 
      key: 'type',
      width: 100,
      render: (v) => <Text>{v || 'Standard'}</Text>
    },
    { 
      title: 'Status', 
      dataIndex: 'status', 
      key: 'status',
      width: 100,
      render: (s) => <StatusTag status={s} />
    },
    { 
      title: 'Budget', 
      dataIndex: 'budget', 
      key: 'budget',
      width: 100,
      render: (v, r) => {
        const budget = v || r.Budget || r.meta_ads_config?.daily_budget || r.google_ads_config?.daily_budget || 0;
        return <Text>${Number(budget).toLocaleString()}</Text>;
      }
    },
    { 
      title: 'Spent', 
      dataIndex: 'spent', 
      key: 'spent',
      width: 100,
      render: (v) => <Text>${Number(v || 0).toLocaleString()}</Text>
    },
    { 
      title: 'Impressions', 
      dataIndex: 'impressions', 
      key: 'impressions',
      width: 110,
      render: (v) => <Text>{Number(v || 0).toLocaleString()}</Text>
    },
    { 
      title: 'Clicks', 
      dataIndex: 'clicks', 
      key: 'clicks',
      width: 80,
      render: (v) => <Text>{Number(v || 0).toLocaleString()}</Text>
    },
    { 
      title: 'CTR', 
      dataIndex: 'ctr', 
      key: 'ctr',
      width: 80,
      render: (v) => <Text>{(Number(v) || 0).toFixed(2)}%</Text>
    },
    { 
      title: 'CPC', 
      dataIndex: 'cpc', 
      key: 'cpc',
      width: 80,
      render: (v) => <Text>${(Number(v) || 0).toFixed(2)}</Text>
    },
    { 
      title: 'Conversions', 
      dataIndex: 'conversions', 
      key: 'conversions',
      width: 100,
      render: (v) => <Text>{Number(v || 0).toLocaleString()}</Text>
    },
    { 
      title: 'Bounce Rate', 
      dataIndex: 'bounce_rate', 
      key: 'bounce_rate',
      width: 100,
      render: (v) => <Text>{(Number(v) || 0).toFixed(2)}%</Text>
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (record) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => editCampaign(record)}
            title="Edit Campaign"
            style={{ color: '#1890ff' }}
          />
          <Button
            type="text"
            size="small"
            icon={record.status === 'active' ? <StopOutlined /> : <PlayCircleOutlined />}
            onClick={() => stopCampaign(record.id, record.name, record.status, record._source)}
            title={record.status === 'active' ? 'Pause Campaign' : 'Reactivate Campaign'}
            style={{ color: record.status === 'active' ? '#faad14' : '#52c41a' }}
          />
          <Button
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => deleteCampaign(record.id, record.name, record._source)}
            danger
            title="Delete Campaign"
          />
        </div>
      )
    }
  ];

  // Column definitions for Email campaigns
  const emailColumns = [
    { 
      title: 'Campaign Name', 
      dataIndex: 'name', 
      key: 'name',
      width: 200,
      fixed: 'left',
      render: (v) => <Text strong style={{ color: '#f59e0b' }}>{v || 'Untitled'}</Text>
    },
    { 
      title: 'Location', 
      dataIndex: 'location', 
      key: 'location',
      width: 120,
      render: (v) => <Text type="secondary">{v || '-'}</Text>
    },
    { 
      title: 'Status', 
      dataIndex: 'status', 
      key: 'status',
      width: 100,
      render: (s) => <StatusTag status={s} />
    },
    { 
      title: 'Sent', 
      dataIndex: 'sent', 
      key: 'sent',
      width: 100,
      render: (v) => <Text>{Number(v || 0).toLocaleString()}</Text>
    },
    { 
      title: 'Opened', 
      dataIndex: 'opened', 
      key: 'opened',
      width: 100,
      render: (v) => <Text>{Number(v || 0).toLocaleString()}</Text>
    },
    { 
      title: 'Open Rate', 
      dataIndex: 'open_rate', 
      key: 'open_rate',
      width: 100,
      render: (v) => <Text>{(Number(v) || 0).toFixed(2)}%</Text>
    },
    { 
      title: 'Clicks', 
      dataIndex: 'clicks', 
      key: 'clicks',
      width: 80,
      render: (v) => <Text>{Number(v || 0).toLocaleString()}</Text>
    },
    { 
      title: 'CTR', 
      dataIndex: 'ctr', 
      key: 'ctr',
      width: 80,
      render: (v) => <Text>{(Number(v) || 0).toFixed(2)}%</Text>
    },
    { 
      title: 'Bounce Rate', 
      dataIndex: 'bounce_rate', 
      key: 'bounce_rate',
      width: 100,
      render: (v) => <Text>{(Number(v) || 0).toFixed(2)}%</Text>
    },
    { 
      title: 'Unsubscribes', 
      dataIndex: 'unsubscribes', 
      key: 'unsubscribes',
      width: 100,
      render: (v) => <Text>{Number(v || 0).toLocaleString()}</Text>
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (record) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => editCampaign(record)}
            title="Edit Campaign"
            style={{ color: '#1890ff' }}
          />
          <Button
            type="text"
            size="small"
            icon={record.status === 'active' ? <StopOutlined /> : <PlayCircleOutlined />}
            onClick={() => stopCampaign(record.id, record.name, record.status, record._source)}
            title={record.status === 'active' ? 'Pause Campaign' : 'Reactivate Campaign'}
            style={{ color: record.status === 'active' ? '#faad14' : '#52c41a' }}
          />
          <Button
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => deleteCampaign(record.id, record.name, record._source)}
            danger
            title="Delete Campaign"
          />
        </div>
      )
    }
  ];

  // Column definitions for SMS campaigns
  const smsColumns = [
    { 
      title: 'Campaign Name', 
      dataIndex: 'name', 
      key: 'name',
      width: 200,
      fixed: 'left',
      render: (v) => <Text strong style={{ color: '#8b5cf6' }}>{v || 'Untitled'}</Text>
    },
    { 
      title: 'Location', 
      dataIndex: 'location', 
      key: 'location',
      width: 120,
      render: (v) => <Text type="secondary">{v || '-'}</Text>
    },
    { 
      title: 'Status', 
      dataIndex: 'status', 
      key: 'status',
      width: 100,
      render: (s) => <StatusTag status={s} />
    },
    { 
      title: 'Sent', 
      dataIndex: 'sent', 
      key: 'sent',
      width: 100,
      render: (v) => <Text>{Number(v || 0).toLocaleString()}</Text>
    },
    { 
      title: 'Delivered', 
      dataIndex: 'delivered', 
      key: 'delivered',
      width: 100,
      render: (v) => <Text>{Number(v || 0).toLocaleString()}</Text>
    },
    { 
      title: 'Delivery Rate', 
      dataIndex: 'delivery_rate', 
      key: 'delivery_rate',
      width: 110,
      render: (v) => <Text>{(Number(v) || 0).toFixed(2)}%</Text>
    },
    { 
      title: 'Clicks', 
      dataIndex: 'clicks', 
      key: 'clicks',
      width: 80,
      render: (v) => <Text>{Number(v || 0).toLocaleString()}</Text>
    },
    { 
      title: 'CTR', 
      dataIndex: 'ctr', 
      key: 'ctr',
      width: 80,
      render: (v) => <Text>{(Number(v) || 0).toFixed(2)}%</Text>
    },
    { 
      title: 'Bounce Rate', 
      dataIndex: 'bounce_rate', 
      key: 'bounce_rate',
      width: 100,
      render: (v) => <Text>{(Number(v) || 0).toFixed(2)}%</Text>
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (record) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => editCampaign(record)}
            title="Edit Campaign"
            style={{ color: '#1890ff' }}
          />
          <Button
            type="text"
            size="small"
            icon={record.status === 'active' ? <StopOutlined /> : <PlayCircleOutlined />}
            onClick={() => stopCampaign(record.id, record.name, record.status, record._source)}
            title={record.status === 'active' ? 'Pause Campaign' : 'Reactivate Campaign'}
            style={{ color: record.status === 'active' ? '#faad14' : '#52c41a' }}
          />
          <Button
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => deleteCampaign(record.id, record.name, record._source)}
            danger
            title="Delete Campaign"
          />
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <Input
            placeholder="Search campaigns or locations..."
            prefix={<SearchOutlined />}
            style={{ width: 280 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Select
            placeholder="All Platforms"
            style={{ width: 150 }}
            allowClear
            options={[
              { value: 'meta', label: 'Meta' },
              { value: 'google', label: 'Google' },
              { value: 'email', label: 'Email' },
              { value: 'sms', label: 'SMS' },
            ]}
          />
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/client/campaigns/new')}>
          Create Campaign
        </Button>
      </div>

      {/* Meta Campaigns */}
      <CampaignSection
        title="Meta Campaigns"
        icon={<img src="/Meta-Emblem.png" alt="Meta" style={{ width: 20, height: 20 }} />}
        color="#0081FB"
        campaigns={groupedCampaigns.meta}
        columns={adColumns}
        count={groupedCampaigns.meta.length}
      />

      {/* Instagram Campaigns */}
      <CampaignSection
        title="Instagram Campaigns"
        icon={<img src="/ig.jpg" alt="Instagram" style={{ width: 20, height: 20, borderRadius: '4px' }} />}
        color="#E4405F"
        campaigns={groupedCampaigns.instagram}
        columns={adColumns}
        count={groupedCampaigns.instagram.length}
      />

      {/* Google Campaigns */}
      <CampaignSection
        title="Google Campaigns"
        icon={
          <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
            <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
            <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
            <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
            <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
          </svg>
        }
        color="#4285f4"
        campaigns={groupedCampaigns.google}
        columns={adColumns}
        count={groupedCampaigns.google.length}
      />

      {/* Email Campaigns */}
      <CampaignSection
        title="Email Campaigns"
        icon={<MailOutlined />}
        color="#f59e0b"
        campaigns={groupedCampaigns.email}
        columns={emailColumns}
        count={groupedCampaigns.email.length}
      />

      {/* SMS Campaigns */}
      <CampaignSection
        title="SMS Campaigns"
        icon={<MessageOutlined />}
        color="#8b5cf6"
        campaigns={groupedCampaigns.sms}
        columns={smsColumns}
        count={groupedCampaigns.sms.length}
      />
    </div>
  );
};

export default Dashboard;
